import { useTranslation } from 'next-i18next'
import React from 'react'
import useLocalizedPath from './useLocalizedPath'
import { seoPages } from '@/content/seo/manifest'
import { SeoLocale } from '@/lib/seo/constants'
import { useSeoLocale } from '@/lib/seo/useSeoLocale'

export interface Menu {
  url: string,
  name: string,
  children?: Menu[],
}

/**
 * The SEO pages below each category in one locale, taken from the generated manifest so
 * the menu and the sitemap cannot disagree about what exists. Nests by url:
 * /dack/pickup holds /dack/pickup/dodge-ram.
 */
const childrenByParent = (locale: SeoLocale): Map<string, Menu[]> => {
  const byParent = new Map<string, Menu[]>()

  // The manifest omits a locale's url and label where they match Swedish, so normalise
  // once here rather than falling back at every use.
  const localized = seoPages.flatMap((page) => {
    const url = page.url[locale]
    return url ? [{ url, name: page.navLabel[locale] ?? page.navLabel.sv }] : []
  })

  // Deepest first, so a page's own children are already assembled when it is placed.
  ;[...localized]
    .sort((a, b) => b.url.split('/').length - a.url.split('/').length)
    .forEach(({ url, name }) => {
      const parent = url.slice(0, url.lastIndexOf('/'))
      const children = byParent.get(url)
      const entry: Menu = {
        url,
        name,
        ...(children?.length ? { children } : {}),
      }
      const siblings = byParent.get(parent)
      if (siblings) siblings.push(entry)
      else byParent.set(parent, [entry])
    })

  return byParent
}

/**
 * Built once at import rather than per render. The manifest is a build-time constant, so
 * only the locale varies — and useMenu is called from the header, the mobile header and
 * the footer, on every page in the app.
 */
const seoChildrenByParent: Record<SeoLocale, Map<string, Menu[]>> = {
  sv: childrenByParent('sv'),
  en: childrenByParent('en'),
}

const useMenu = () => {
  const { t } = useTranslation('common')
  const seoLocale = useSeoLocale()
  const getLocalizedPath = useLocalizedPath()

  // useMemo, not useState in an effect: the menu used to be empty in the server-rendered
  // HTML and only appear after hydration, which hides every link from crawlers. Nothing
  // here is async — t and getLocalizedPath are both synchronous.
  return React.useMemo<Menu[]>(() => {
    const seoChildren = seoChildrenByParent[seoLocale]

    // A locale with no SEO copy at all would give dead links for the submenus and the
    // brands entry, which exists only as an SEO page.
    const hasSeoPages = seoChildren.size > 0

    const withChildren = (key: string): Menu => {
      const url = getLocalizedPath(key)
      const children = seoChildren.get(url) ?? []
      return {
        url,
        name: t(`menu.${key}`),
        ...(children.length ? { children } : {}),
      }
    }

    return [
      withChildren('wheels'),
      withChildren('rims'),
      withChildren('tyres'),
      { url: getLocalizedPath('accessories'), name: t('menu.accessories') },
      ...(hasSeoPages ? [withChildren('brands')] : []),
      { url: getLocalizedPath('about'), name: t('menu.about') },
      { url: getLocalizedPath('contact'), name: t('menu.contact') },
    ]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, seoLocale])
}

export default useMenu
